<script lang="ts">
	/**
	 * Walker — a self-contained simulated person on the presence grid.
	 *
	 * Fully ports the autonomous movement, bezier path sampling, and trail-ghost
	 * system from the presence-ui-tests prototype. Works in fractional (0..1)
	 * coordinate space so the position scales automatically with gridSize.
	 *
	 * Props:
	 *   color      — CSS hex/rgb color for the walker bubble
	 *   energetic  — faster, more erratic movement; orange glow
	 *   gridSize   — current rendered grid width (px); pass from PresenceGrid
	 */

	import { onMount, onDestroy } from 'svelte';

	// ── Constants (identical to presence-ui-tests/script.js) ─────────────────
	const MOVE_DURATION_MS = 900;
	const MOVE_CHANCE = 0.58;
	const ENERGETIC_MOVE_CHANCE = 0.82;
	const CROSS_ROOM_CHANCE = 0.1;
	const ENERGETIC_CROSS_ROOM_CHANCE = 0.22;
	const MAX_TURN = 0.75;
	const ENERGETIC_MAX_TURN = 1.1;
	const SEGMENT_SIZE = 0.45;
	const SPEED = 5.8; // cells / second
	const ENERGETIC_SPEED = 8.8;

	// ── Props ─────────────────────────────────────────────────────────────────
	export let color: string;
	export let energetic: boolean = false;
	export let gridSize: number;

	// ── Types ─────────────────────────────────────────────────────────────────
	interface Pt {
		x: number;
		y: number;
	}

	// ── Position (fractional 0..1 — automatically scales with gridSize) ───────
	let xFrac = 0.5;
	let yFrac = 0.7;

	// Derived pixel positions for CSS rendering
	$: xPx = xFrac * gridSize;
	$: yPx = yFrac * gridSize;

	// ── Visual state ──────────────────────────────────────────────────────────
	let facingLeft = false;
	let isMoving = false;
	let isRunning = false; // mdi-run vs mdi-human-handsdown
	let stepDurationMs = 200;
	let visible = false;

	// ── Internal timers / tokens ──────────────────────────────────────────────
	let moveTimers: ReturnType<typeof setTimeout>[] = [];
	let iconTimer: ReturnType<typeof setTimeout> | null = null;
	let tickTimer: ReturnType<typeof setInterval> | null = null;
	let moveToken = 0;

	// ── Sim state (heading, pause ticks, roaming budget) ─────────────────────
	let sim = {
		heading: Math.random() * Math.PI * 2,
		pauseTicks: Math.floor(Math.random() * 3),
		roamingBudget: 2 + Math.floor(Math.random() * 3),
	};

	// ── Derived metrics ───────────────────────────────────────────────────────
	$: cell = gridSize / 16;
	$: indicatorSize = Math.max(32, (gridSize / 16) * 1.5);
	$: indicatorIconSize = Math.max(20, (gridSize / 16) * 1.1); // larger without circle

	// ── Math helpers ──────────────────────────────────────────────────────────
	function clamp(v: number, lo: number, hi: number) {
		return Math.max(lo, Math.min(hi, v));
	}
	function rf(lo: number, hi: number) {
		return lo + Math.random() * (hi - lo);
	}
	function ri(lo: number, hi: number) {
		return Math.floor(rf(lo, hi + 1));
	}
	function dist(a: Pt, b: Pt) {
		return Math.hypot(b.x - a.x, b.y - a.y);
	}
	function clampPt(p: Pt): Pt {
		return {
			x: clamp(p.x, cell / 2, gridSize - cell / 2),
			y: clamp(p.y, cell / 2, gridSize - cell / 2),
		};
	}
	function normAngle(a: number) {
		const t = Math.PI * 2;
		let n = a % t;
		if (n < 0) n += t;
		return n;
	}
	function angleDelta(from: number, to: number) {
		let d = normAngle(to) - normAngle(from);
		if (d > Math.PI) d -= Math.PI * 2;
		if (d < -Math.PI) d += Math.PI * 2;
		return d;
	}
	function nearEdge(p: Pt, pad = 1.1) {
		const b = pad * cell;
		return p.x <= b || p.y <= b || p.x >= gridSize - b || p.y >= gridSize - b;
	}
	function randomCellPx(): Pt {
		const col = Math.floor(Math.random() * 16);
		const row = Math.floor(Math.random() * 16);
		return { x: col * cell + cell / 2, y: row * cell + cell / 2 };
	}
	function pxPos(): Pt {
		return { x: xFrac * gridSize, y: yFrac * gridSize };
	}
	function setPosFrac(p: Pt) {
		xFrac = p.x / gridSize;
		yFrac = p.y / gridSize;
	}

	// ── Route building ────────────────────────────────────────────────────────
	function buildRoute(start: Pt): Pt[] {
		const route: Pt[] = [{ ...start }];
		const crossChance = energetic ? ENERGETIC_CROSS_ROOM_CHANCE : CROSS_ROOM_CHANCE;
		const doCross = sim.roamingBudget <= 0 && Math.random() < crossChance;

		if (doCross) {
			const { heading } = sim;
			const horiz = Math.abs(Math.cos(heading)) >= Math.abs(Math.sin(heading));
			const target = horiz
				? {
						x: Math.cos(heading) >= 0 ? gridSize - cell / 2 : cell / 2,
						y: clamp(start.y + rf(-cell * 2.25, cell * 2.25), cell / 2, gridSize - cell / 2),
					}
				: {
						x: clamp(start.x + rf(-cell * 2.25, cell * 2.25), cell / 2, gridSize - cell / 2),
						y: Math.sin(heading) >= 0 ? gridSize - cell / 2 : cell / 2,
					};
			route.push(
				clampPt({
					x: start.x + (target.x - start.x) * 0.35 + rf(-cell * 1.1, cell * 1.1),
					y: start.y + (target.y - start.y) * 0.35 + rf(-cell * 1.1, cell * 1.1),
				}),
			);
			if (dist(start, target) > cell * 8) {
				route.push(
					clampPt({
						x: start.x + (target.x - start.x) * 0.68 + rf(-cell, cell),
						y: start.y + (target.y - start.y) * 0.68 + rf(-cell, cell),
					}),
				);
			}
			route.push(target);
			sim.roamingBudget = energetic ? ri(2, 4) : ri(3, 5);
		} else {
			const center = { x: gridSize / 2, y: gridSize / 2 };
			let heading = normAngle(sim.heading + rf(-0.35, 0.35));
			let anchor = start;
			const turnLimit = energetic ? ENERGETIC_MAX_TURN : MAX_TURN;
			const segments = energetic ? ri(2, 4) : ri(2, 3);
			for (let i = 0; i < segments; i++) {
				const ch = Math.atan2(center.y - anchor.y, center.x - anchor.x);
				heading = normAngle(heading + angleDelta(heading, ch) * (energetic ? 0.08 : 0.13));
				heading = normAngle(heading + rf(-turnLimit, turnLimit) * (i === 0 ? 1 : 0.7));
				if (nearEdge(anchor)) heading = normAngle(heading + angleDelta(heading, ch) * 0.28);
				const sc = energetic ? rf(1.6, 3.9) : rf(1, 2.8);
				const next = clampPt({
					x: anchor.x + Math.cos(heading) * sc * cell,
					y: anchor.y + Math.sin(heading) * sc * cell,
				});
				if (dist(anchor, next) < cell * 0.28) continue;
				route.push(next);
				anchor = next;
			}
			sim.heading = heading;
			sim.roamingBudget = Math.max(0, sim.roamingBudget - 1);
		}

		const cleaned: Pt[] = [route[0]];
		for (let i = 1; i < route.length; i++) {
			if (dist(cleaned[cleaned.length - 1], route[i]) >= cell * 0.35) cleaned.push(route[i]);
		}
		if (cleaned.length === 1) cleaned.push(randomCellPx());
		return cleaned;
	}

	// ── Bezier path sampling ──────────────────────────────────────────────────
	function sampleQuadSeg(a: Pt, b: Pt, bend: number): Pt[] {
		const sl = dist(a, b);
		const n = Math.max(2, Math.ceil(sl / (cell * SEGMENT_SIZE)));
		const dx = b.x - a.x;
		const dy = b.y - a.y;
		const mag = Math.max(1, Math.hypot(dx, dy));
		const ctrl = clampPt({
			x: (a.x + b.x) / 2 + (-dy / mag) * bend,
			y: (a.y + b.y) / 2 + (dx / mag) * bend,
		});
		const pts: Pt[] = [];
		for (let i = 1; i <= n; i++) {
			const t = i / n;
			const o = 1 - t;
			pts.push(
				clampPt({
					x: o * o * a.x + 2 * o * t * ctrl.x + t * t * b.x,
					y: o * o * a.y + 2 * o * t * ctrl.y + t * t * b.y,
				}),
			);
		}
		return pts;
	}

	function samplePath(route: Pt[]): Pt[] {
		const pts: Pt[] = [route[0]];
		let prevBend = 0;
		for (let i = 1; i < route.length; i++) {
			const from = route[i - 1];
			const to = route[i];
			const sl = dist(from, to);
			if (sl < 1) continue;
			const maxBend = energetic ? cell * 0.95 : cell * 0.72;
			const bendLim = Math.max(cell * 0.16, Math.min(maxBend, sl * 0.32));
			const bend = prevBend * 0.35 + rf(-bendLim, bendLim);
			prevBend = bend;
			sampleQuadSeg(from, to, bend).forEach((p) => pts.push(p));
		}
		return pts.length > 1 ? pts : [route[0], randomCellPx()];
	}

	// ── Path utilities ────────────────────────────────────────────────────────
	function pathLen(pts: Pt[]): number {
		let l = 0;
		for (let i = 1; i < pts.length; i++) l += dist(pts[i - 1], pts[i]);
		return l;
	}

	function ptTimes(pts: Pt[], totalMs: number): number[] {
		const total = pathLen(pts);
		if (total <= 0.01) return pts.map((_, i) => (i === 0 ? 0 : totalMs));
		const times = [0];
		let trav = 0;
		for (let i = 1; i < pts.length; i++) {
			trav += dist(pts[i - 1], pts[i]);
			times.push((trav / total) * totalMs);
		}
		return times;
	}

	// ── Movement ──────────────────────────────────────────────────────────────
	function clearTimers() {
		moveTimers.forEach((t) => clearTimeout(t));
		moveTimers = [];
		isMoving = false;
	}

	function setIdle() {
		isRunning = false;
		isMoving = false;
		facingLeft = false;
	}

	function setMoving(dx: number, durationMs: number) {
		// Running-person icon animation disabled (keep fa-person-simple)
		// isRunning = true;
		isMoving = true;
		if (Math.abs(dx) >= 0.25) facingLeft = dx < 0;
		if (iconTimer) clearTimeout(iconTimer);
		iconTimer = setTimeout(() => {
			setIdle();
			iconTimer = null;
		}, durationMs);
	}

	function startMove(pathPts: Pt[]): boolean {
		if (pathPts.length < 2) return false;
		const len = pathLen(pathPts);
		const speed = energetic ? ENERGETIC_SPEED : SPEED;
		const durFromDist = (len / Math.max(1, speed * cell)) * 1000;
		const minDur = MOVE_DURATION_MS * (energetic ? 0.9 : 0.8);
		const maxDur = MOVE_DURATION_MS * (energetic ? 3.1 : 2.2);
		const totalMs = Math.max(minDur, Math.min(maxDur, durFromDist));
		const times = ptTimes(pathPts, totalMs);
		let longestStep = 0;
		for (let i = 1; i < times.length; i++)
			longestStep = Math.max(longestStep, times[i] - times[i - 1]);
		stepDurationMs = Math.max(60, Math.round(longestStep * 1.05));

		clearTimers();
		const token = moveToken + 1;
		moveToken = token;
		isMoving = true;
		setMoving(pathPts[pathPts.length - 1].x - pathPts[0].x, totalMs + 80);

		for (let i = 1; i < pathPts.length; i++) {
			const pt = pathPts[i];
			const isLast = i === pathPts.length - 1;
			const timer = setTimeout(() => {
				if (moveToken !== token) return;
				const prevXPx = xFrac * gridSize;
				if (Math.abs(pt.x - prevXPx) >= 0.25) facingLeft = pt.x < prevXPx;
				setPosFrac(pt);
				if (isLast) {
					isMoving = false;
					if (pathPts.length >= 2) {
						const prev = pathPts[pathPts.length - 2];
						const next = pathPts[pathPts.length - 1];
						sim.heading = normAngle(Math.atan2(next.y - prev.y, next.x - prev.x));
					}
					const traveledCells = len / Math.max(1, cell);
					const basePause = energetic ? ri(0, 1) : ri(0, 2);
					sim.pauseTicks = Math.min(
						energetic ? 2 : 3,
						basePause + (traveledCells > (energetic ? 8 : 6) ? 1 : 0),
					);
				}
			}, Math.round(times[i]));
			moveTimers.push(timer);
		}
		return true;
	}

	function tick() {
		if (isMoving) return;
		if (sim.pauseTicks > 0) {
			sim.pauseTicks -= 1;
			return;
		}
		if (Math.random() > (energetic ? ENERGETIC_MOVE_CHANCE : MOVE_CHANCE)) return;
		if (cell <= 0 || gridSize <= 0) return;
		startMove(samplePath(buildRoute(pxPos())));
	}

	// ── Lifecycle ─────────────────────────────────────────────────────────────
	onMount(() => {
		if (!gridSize) return;
		// Spawn in detectable area (rows 5–14 avoids the deadzone at the top)
		const localCell = gridSize / 16;
		const col = Math.floor(Math.random() * 16);
		const row = 5 + Math.floor(Math.random() * 10);
		xFrac = (col * localCell + localCell / 2) / gridSize;
		yFrac = (row * localCell + localCell / 2) / gridSize;
		const cx = gridSize / 2;
		const cy = gridSize / 2;
		sim.heading = normAngle(
			Math.atan2(cy - yFrac * gridSize, cx - xFrac * gridSize) + rf(-0.8, 0.8),
		);
		sim.pauseTicks = ri(0, 2);
		sim.roamingBudget = ri(2, 4);
		requestAnimationFrame(() => {
			visible = true;
		});
		tickTimer = setInterval(tick, 1200);
	});

	onDestroy(() => {
		clearTimers();
		if (iconTimer) clearTimeout(iconTimer);
		if (tickTimer) clearInterval(tickTimer);
		moveToken += 1; // cancel any in-flight timers that read moveToken
	});
</script>

<!-- ── Walker element ────────────────────────────────────────────────────── -->
<span
	class="walker"
	class:walker-moving={isMoving}
	class:walker-energetic={energetic}
	class:walker-facing-left={facingLeft}
	class:walker-faded={!visible}
	style:left="{xPx}px"
	style:top="{yPx}px"
	style:--walker-color={color}
	style:--walker-step-duration="{stepDurationMs}ms"
	style:width="{indicatorSize}px"
	style:height="{indicatorSize}px"
	aria-hidden="true"
>
	<!-- Running icon disabled — same as Dot.svelte -->
	<i
		class="fas fa-person-simple walker-icon"
		style:font-size="{indicatorIconSize}px"
		aria-hidden="true"
	></i>
</span>

<style>
	/* ── Walker base (icon-only — no circular background) ──────────────────── */
	.walker {
		position: absolute;
		color: var(--walker-color);
		display: flex;
		align-items: center;
		justify-content: center;
		transform: translate(-50%, -50%);
		z-index: 10;
		pointer-events: none;
		opacity: 1;
		transition-property: left, top, opacity;
		transition-duration: var(--walker-step-duration, 0.9s), var(--walker-step-duration, 0.9s), 1.6s;
		transition-timing-function: ease-in-out, ease-in-out, ease-out;
	}

	.walker.walker-moving {
		z-index: 12;
	}

	.walker :global(.walker-icon) {
		line-height: 1;
	}

	/* Flip the icon horizontally when walking left */
	.walker.walker-facing-left :global(.walker-icon) {
		transform: scaleX(-1);
	}

	/* Fade-in / fade-out (toggled via visible flag) */
	.walker-faded {
		opacity: 0;
	}

	/* Energetic jitter on running icon — disabled
	.walker-energetic :global(.walker-icon) {
		position: relative;
		animation: energetic-jitter 90ms steps(1, end) infinite;
	}
	@keyframes energetic-jitter {
		0% { left: 0; top: 0; }
		12% { left: -1.1px; top: 0.5px; }
		24% { left: 0.9px; top: -0.8px; }
		38% { left: -0.5px; top: -1.2px; }
		52% { left: 1.2px; top: 0.3px; }
		66% { left: -1.3px; top: -0.2px; }
		82% { left: 0.6px; top: 1px; }
		100% { left: -0.2px; top: -0.6px; }
	}
	*/
</style>
